import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Leaf,
  Trees,
  Clock,
  Heart,
  Home,
  Wind,
  Sun,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';

const AboutUs = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const values = [
    {
      icon: <Leaf size={32} />,
      title: 'Slow Living',
      description:
        'We design experiences that allow time to stretch — mornings without alarms, evenings without urgency.',
    },
    {
      icon: <Trees size={32} />,
      title: 'Respect for Nature',
      description:
        'The forest is not a backdrop. It is the host. Every decision is made with minimal ecological impact.',
    },
    {
      icon: <Clock size={32} />,
      title: 'Unhurried Time',
      description:
        'No packed itineraries. No constant activity. Guests are encouraged to simply exist.',
    },
    {
      icon: <Heart size={32} />,
      title: 'Care Over Luxury',
      description:
        'Luxury here is silence, warmth, clean air, thoughtful food, and genuine hospitality.',
    },
    {
      icon: <Home size={32} />,
      title: 'Few, Not Many',
      description:
        'We intentionally limit rooms and guests to preserve privacy, quiet, and the forest’s balance.',
    },
    {
      icon: <Wind size={32} />,
      title: 'Mental Reset',
      description:
        'Bagh Home exists for those seeking clarity — away from noise, pressure, and performance.',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#F9F8F6' }}>
      <Navbar scrolled={scrolled} />

      {/* Hero */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <img
          src="https://res.cloudinary.com/dwwjvrlvg/image/upload/v1768661240/0e34e316-a735-44de-804d-01e5e68a20fd_nrhnra.jpg"
          alt="The Bagh Home"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/45" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center text-white px-4 max-w-4xl"
        >
          <p className="overline text-white/80 mb-4">OUR STORY</p>
          <h1
            className="text-5xl md:text-7xl font-bold mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            About The Bagh Home
          </h1>
          <p className="text-xl md:text-2xl font-light opacity-90">
            A home built around silence, trees, and time
          </p>
        </motion.div>
      </section>

      {/* Philosophy */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Sun className="mx-auto mb-6" size={44} style={{ color: '#8F9E8B' }} />
            <h2
              className="text-3xl md:text-4xl font-bold mb-8"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              Why We Exist
            </h2>
            <p className="text-lg leading-relaxed mb-6" style={{ color: '#64748B' }}>
              The Bagh Home was created as a response to modern exhaustion — the 
              constant rush, the noise, the pressure to always be productive.
            </p>
            <p className="text-lg leading-relaxed mb-6" style={{ color: '#64748B' }}>
              We believe true rest comes not from distraction, but from stillness. 
              From waking up with natural light. From listening to birds instead of notifications.
            </p>
            <p
              className="text-xl italic"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              “This is not a place to do more; it is a place to be.”
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding" style={{ background: 'white' }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="overline text-[#8F9E8B] mb-4">OUR VALUES</p>
            <h2
              className="text-4xl md:text-5xl font-bold"
              style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
            >
              What Guides Us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl"
                style={{ background: '#F9F8F6' }}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                  style={{ background: 'white', color: '#1A3C34' }}
                >
                  {value.icon}
                </div>
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ fontFamily: 'Playfair Display, serif', color: '#1A3C34' }}
                >
                  {value.title}
                </h3>
                <p style={{ color: '#64748B' }}>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="section-padding" style={{ background: '#1A3C34' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Trees className="mx-auto mb-6" size={44} style={{ color: '#8F9E8B' }} />
            <h2
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              A Home, Not a Hotel
            </h2>
            <p className="text-lg text-white/80 mb-8">
              The Bagh Home is intentionally small, deeply personal, and rooted 
              in its surroundings. If you are seeking parties, crowds, or constant entertainment;
              this may not be for you.
            </p>
            <p className="text-lg text-white/80 mb-10">
              But if you are seeking rest, reflection, and reconnection —
              we will welcome you quietly.
            </p>
            <Link to="/rooms">
              <button className="btn-primary">View Our Rooms</button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
